'use client'

import { useEffect, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useCookies } from 'next-client-cookies'
import { AlertCircle, LockIcon, LogInIcon, MailIcon } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/ui/password-input'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { setValidationErrors, getErrorMessage } from '@/lib/utils'
import { getCookieOptions } from '@/lib/cookie-utils'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { useLoginStore, useMeStore } from '@/store'
import { EMAIL_REGEX, PASSWORD_REGEX } from '@/constants'
import { LoginParamsType, TokensType } from '@/types/auth'

const LoginForm = () => {
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const redirectPath = searchParams.get('next') || null
  const cookies = useCookies()
  const { loginState, login } = useLoginStore()
  const { meState, getMe, reset } = useMeStore()
  const [authError, setAuthError] = useState<string | null>(null)
  const [formDisabled, setFormDisabled] = useState<boolean>(false)

  const formSchema = z.object({
    email: z
      .string()
      .trim()
      .transform(val => val.toLowerCase())
      .refine(val => EMAIL_REGEX.test(val), {
        message: t('invalid_email')
      }),
    password: z
      .string()
      .min(6, { message: t('min_value', { value: 6 }) })
      .max(32, { message: t('max_value', { value: 32 }) })
      .refine(val => PASSWORD_REGEX.test(val), {
        message: t('password_pattern')
      })
      .refine((val: string) => !/\s/.test(val), { message: t('password_pattern') }),
    rememberMe: z.boolean().optional()
  })

  const { mutate } = useMutation({
    mutationFn: async (data: LoginParamsType) => {
      return await login(data)
    },
    onSuccess: async (response: TokensType) => {
      setAuthError(null)
      const rememberMe = cookies.get('rememberMe') === 'true'
      const cookieOptions = getCookieOptions(rememberMe)
      cookies.set('accessToken', response.accessToken, cookieOptions)
      cookies.set('refreshToken', response.refreshToken, cookieOptions)
      await getMe()
      if (redirectPath) {
        router.push(redirectPath)
      } else {
        router.push('/')
      }

      toast.success(t('login_success'))
    },
    onError: (error: any) => {
      const message = getErrorMessage(error, t('something_went_wrong'))
      if (error.response?.status === 401) {
        setAuthError(message)
      } else if (error.response?.status === 423) {
        setAuthError(message)
      } else if (error.response?.status === 422) {
        setValidationErrors(error, form.setError)
        setAuthError(null)
      } else {
        setAuthError(null)
        reset()
      }
    }
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false
    }
  })

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    const { rememberMe, ...loginParams } = data
    cookies.set('rememberMe', rememberMe ? 'true' : 'false', getCookieOptions(rememberMe))
    mutate(loginParams)
  }

  // Reset auth state when the login form mounts so the form is never stuck disabled
  // (e.g., after a forced logout via soft navigation)
  useEffect(() => {
    reset()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (meState.isLoading || meState.response !== null || loginState.isLoading) {
      setFormDisabled(true)
    } else {
      setFormDisabled(false)
    }
  }, [form.formState, meState.isLoading, meState.response, loginState.isLoading])

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <Form {...form}>
        {authError && (
          <Alert variant="destructive" className="mb-4" onClose={() => setAuthError(null)}>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('error')}</AlertTitle>
            <AlertDescription>{authError}</AlertDescription>
          </Alert>
        )}
        <div className="flex flex-col gap-1">
          <div className="grid gap-1">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="login-email" required>
                    {t('email')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      id="login-email"
                      type="email"
                      autoComplete="username"
                      placeholder="mail@example.com"
                      icon={<MailIcon className="h-4 w-4" />}
                      autoFocus
                      disabled={formDisabled}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel htmlFor="login-password" required>
                      {t('password')}
                    </FormLabel>
                    <div className="text-xs text-gray-500 hover:underline">
                      <Link href="/password" tabIndex={-1}>
                        {t('forgot_password')}
                      </Link>
                    </div>
                  </div>
                  <FormControl>
                    <PasswordInput
                      id="login-password"
                      type="password"
                      autoComplete="current-password"
                      placeholder="P@ssw0rd."
                      icon={<LockIcon className="h-4 w-4" />}
                      disabled={formDisabled}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="rememberMe"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="flex flex-row items-center space-x-2">
                      <Switch
                        id="login-remember-me"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={formDisabled}
                      />
                      <Label htmlFor="login-remember-me">{t('remember_me')}</Label>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <Button type="submit" disabled={loginState.isLoading || formDisabled} loading={loginState.isLoading}>
            <LogInIcon className="h-4 w-4" />
            {t('login')}
          </Button>
        </div>
      </Form>
    </form>
  )
}

export default LoginForm
